/**
 * Los eventos propios de la tienda en Google Analytics. Lo demas (vistas,
 * desplazamiento, busqueda) lo mide la medicion mejorada de la propiedad.
 *
 * - El embudo de compra, con los nombres de comercio electronico de GA4 para
 *   que la propiedad lo arme sola (Informes → Monetizacion):
 *   `view_item` (ficha) → `add_to_cart` → `begin_checkout` (al entrar al
 *   checkout con carro) → `add_payment_info` (pedido creado, sale a Webpay)
 *   → `purchase`.
 * - `purchase`: el evento de compra de GA4, que la propiedad cuenta como
 *   evento clave y con ingresos. Se envia al ver un pago APROBADO.
 * - `contacto_whatsapp`: cada clic en un boton de WhatsApp, con su origen.
 *   Se marca como evento clave en la propiedad (Administrar → Eventos clave):
 *   buena parte de la venta se cierra por ahi.
 */
import type { Carro, ItemPedido, OrigenWhatsapp, ProductoResumen } from '@elcarpintero/compartido';

import { enviarEvento } from './analytics';

/** Pedidos ya informados desde este navegador: recargar la confirmacion no cuenta dos veces. */
const CLAVE_COMPRAS_INFORMADAS = 'elcarpintero:compras-informadas';
const TOPE_COMPRAS_RECORDADAS = 20;

function comprasInformadas(): string[] {
  try {
    const guardado: unknown = JSON.parse(window.localStorage.getItem(CLAVE_COMPRAS_INFORMADAS) ?? '[]');
    return Array.isArray(guardado) ? guardado.filter((r): r is string => typeof r === 'string') : [];
  } catch {
    return [];
  }
}

function recordarCompra(referencia: string): void {
  try {
    const recordadas = [...comprasInformadas(), referencia].slice(-TOPE_COMPRAS_RECORDADAS);
    window.localStorage.setItem(CLAVE_COMPRAS_INFORMADAS, JSON.stringify(recordadas));
  } catch {
    // Sin almacenamiento: GA igual descarta un `purchase` con el mismo `transaction_id`.
  }
}

/**
 * Un producto como lo pide GA4. Precio y cantidad en paquetes, igual que en
 * `purchase` (VTA-01): asi el embudo suma lo mismo de punta a punta.
 */
function itemDe(producto: ProductoResumen, cantidad: number): Record<string, unknown> {
  return {
    item_id: producto.sku,
    item_name: producto.nombre,
    ...(producto.marca !== null ? { item_brand: producto.marca } : {}),
    price: producto.precio,
    quantity: cantidad,
  };
}

export function registrarVistaProducto(producto: ProductoResumen): void {
  enviarEvento('view_item', {
    currency: 'CLP',
    value: producto.precio,
    items: [itemDe(producto, 1)],
  });
}

export function registrarAgregadoAlCarro(producto: ProductoResumen, cantidad: number): void {
  enviarEvento('add_to_cart', {
    currency: 'CLP',
    value: producto.precio * cantidad,
    items: [itemDe(producto, cantidad)],
  });
}

function eventoDeCarro(nombre: string, carro: Carro, extra: Record<string, unknown> = {}): void {
  enviarEvento(nombre, {
    ...extra,
    currency: 'CLP',
    value: carro.subtotal,
    items: carro.items.map((item) => itemDe(item.producto, item.cantidad)),
  });
}

export function registrarInicioCheckout(carro: Carro): void {
  eventoDeCarro('begin_checkout', carro);
}

/** El pedido ya existe y el navegador sale a Webpay: lo que falta es pagar. */
export function registrarSalidaAPago(carro: Carro): void {
  eventoDeCarro('add_payment_info', carro, { payment_type: 'Webpay' });
}

interface CompraInformable {
  referencia: string;
  total: number;
  /** Las lineas del comprobante; vacias si la confirmacion llego sin el token. */
  items: readonly ItemPedido[];
}

export function registrarCompra({ referencia, total, items }: CompraInformable): void {
  if (comprasInformadas().includes(referencia)) return;
  recordarCompra(referencia);

  const productos = items.reduce((suma, item) => suma + item.subtotal, 0);
  enviarEvento('purchase', {
    transaction_id: referencia,
    value: total,
    currency: 'CLP',
    // Lo que no es producto es el despacho (no hay descuentos ni impuestos aparte).
    ...(items.length > 0 ? { shipping: Math.max(total - productos, 0) } : {}),
    items: items.map((item) => ({
      item_id: item.sku,
      item_name: item.nombre,
      price: item.precioUnitario,
      quantity: item.cantidad,
    })),
  });
}

export function registrarContactoWhatsapp(origen: OrigenWhatsapp): void {
  enviarEvento('contacto_whatsapp', { origen });
}
